// Reports table columns — the domain cells (report identity, folder chip,
// published date, absolute last-entry datetime, entry count) plus the trailing
// kebab, rendered inside the common DataTable. Rows are form templates: a
// "report" is a template plus the submissions collected against it.

import { useTranslation } from 'react-i18next';
import { Chip, Icon, type DataTableColumn } from '@/components/common';
import type { FormTemplate } from '@/api/endpoints/formsApi';
import i18n from '@/i18n/config';
import ReportRowActions from './ReportRowActions';
import { fmtDate } from '@/utils/formatDate';

// Last entry is an ABSOLUTE datetime here (unlike the Forms table's relative
// "40m ago"). Intl emits a comma between date and time, never the design's "·",
// so the two parts are formatted separately and joined through an i18n key —
// the same idiom as peopleColumns' `people_last_today`.
const fmtDateTime = (
  iso: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = fmtDate(iso);
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return t('reports_last_entry', { date, time });
};

const dash = <span className='reports-table__dash'>—</span>;

interface UseReportColumnsArgs {
  /** Kebab View submissions — the same navigation a row click takes. */
  onView: (report: FormTemplate) => void;
  /** Kebab Bookmark / Remove bookmark — the page picks the body value. */
  onBookmark: (report: FormTemplate) => void;
  /** The Bookmarks tab — every row there is bookmarked, so the kebab offers
   *  Remove bookmark (rows carry no per-row flag of their own). */
  bookmarked: boolean;
}

export const useReportColumns = ({
  onView,
  onBookmark,
  bookmarked,
}: UseReportColumnsArgs): DataTableColumn<FormTemplate>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'name',
      label: t('table_header_name'),
      sortable: true,
      width: 'minmax(15rem, 1.6fr)',
      render: (report) => (
        <span className='reports-table__name'>
          <span className='reports-table__icon' aria-hidden='true'>
            <Icon name={report.icon} size={16} />
          </span>
          <span className='reports-table__name-text'>{report.name}</span>
        </span>
      ),
    },
    {
      key: 'folder',
      label: t('table_header_folder'),
      render: (report) =>
        report.folder ? (
          <Chip variant='outline' size='lg' iconStart='folder'>
            {report.folder.title}
          </Chip>
        ) : (
          dash
        ),
    },
    {
      key: 'published',
      label: t('table_header_published_date'),
      sortable: true,
      render: (report) => (
        <span className='reports-table__muted'>{fmtDate(report.publishedDate)}</span>
      ),
    },
    {
      key: 'lastEntry',
      label: t('table_header_last_entry'),
      sortable: true,
      render: (report) => (
        <span className='reports-table__muted'>{fmtDateTime(report.lastEntry, t)}</span>
      ),
    },
    {
      key: 'entries',
      label: t('table_header_entries'),
      sortable: true,
      render: (report) =>
        report.entries === null ? (
          dash
        ) : (
          <span className='reports-table__entries'>
            {new Intl.NumberFormat(i18n.language).format(report.entries)}
          </span>
        ),
    },
    {
      key: 'actions',
      label: '',
      align: 'end',
      width: '4.5rem',
      render: (report) => (
        <ReportRowActions
          onView={() => onView(report)}
          onBookmark={() => onBookmark(report)}
          bookmarked={bookmarked}
        />
      ),
    },
  ];
};
